import { Component, Input, OnChanges, AfterViewInit, OnDestroy, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-new-map',
    templateUrl: './new-map.component.html',
    styleUrls: ['./new-map.component.scss'],
})
export class NewMapComponent implements AfterViewInit, OnChanges, OnDestroy {
    private API_KEY = 'pk.5383e9fb4b08c7783919c3ca31d504f1';
    map: L.Map;
    markers: L.Marker[] = [];
    private resizeObserver: ResizeObserver;

    @ViewChild('mapEl', { static: true }) mapEl: ElementRef<HTMLDivElement>;

    @Input() containerId: string = 'mapId';
    @Input() height: string = '500px';
    @Input() longitud: number;
    @Input() latitud: number;
    @Input() direccion: string;
    @Input() locations: Array<{ lat: number; log: number; popup?: string }>;

    customIcon = L.divIcon({
        className: 'custom-marker',
        html: `
        <svg xmlns="http://www.w3.org/2000/svg" aria-label="Ubicación en el mapa" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
          </svg>
      `,
        iconSize: [30, 30],
    });
    miUbicacionMarker: L.Marker | null = null;
    miUbicacionActivada: Boolean = false;

    constructor() {}

    ngAfterViewInit(): void {
        this.map = L.map(this.mapEl.nativeElement).setView([4.711, -74.0721], 13);

        var basemaps = {
            Calles: L.tileLayer(`https://{s}-tiles.locationiq.com/v2/obk/r/{z}/{x}/{y}.png?key=${this.API_KEY}`),
            Estándar: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'),
        };
        L.control.layers(basemaps).addTo(this.map);
        basemaps.Calles.addTo(this.map);

        // Invalidación inicial (por si el contenedor ya tiene su tamaño final)
        setTimeout(() => {
            this.map.invalidateSize();
        }, 0);

        // Invalidación continua: se dispara cada vez que el contenedor
        // cambia de tamaño (layout todavía acomodándose, tabs, modales, etc.)
        this.resizeObserver = new ResizeObserver(() => {
            this.map?.invalidateSize();
        });
        this.resizeObserver.observe(this.mapEl.nativeElement);

        if (this.latitud && this.longitud) {
            this.addMarker(this.latitud, this.longitud, this.direccion);
        }

        if (this.locations?.length) {
            this.addMarkers(this.locations);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.map) return;

        if (this.latitud !== undefined && this.longitud !== undefined) {
            this.addMarker(this.latitud, this.longitud, this.direccion);
        }

        if (changes['locations'] && this.locations) {
            this.setMarkers(this.locations);
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.map?.remove();
    }

    addMarker(lat: number, lng: number, popupText: string) {
        if (!this.map) return;

        const marker = L.marker([lat, lng], { icon: this.customIcon }).addTo(this.map).bindPopup(popupText);
        this.markers.push(marker);
        this.map.setView([lat, lng], 14);
    }

    addMarkers(locations: Array<{ lat: number; log: number; popup?: string }>) {
        if (!this.map) return;

        locations.forEach((location) => {
            const marker = L.marker([location.lat, location.log], { icon: this.customIcon }).addTo(this.map);
            if (location.popup) marker.bindPopup(location.popup);
            this.markers.push(marker);
        });

        this.fitToMarkers();
    }

    setMarkers(locations: Array<{ lat: number; log: number; popup?: string }>) {
        this.markers.forEach((marker) => this.map.removeLayer(marker));
        this.markers = [];
        this.addMarkers(locations);
    }

    private fitToMarkers(): void {
        if (!this.markers.length) return;

        if (this.markers.length === 1) {
            this.map.setView(this.markers[0].getLatLng(), 14);
            return;
        }

        const group = L.featureGroup(this.markers);
        this.map.fitBounds(group.getBounds(), { padding: [30, 30] });
    }

    async ubicacionActual() {
        if (!navigator.geolocation) {
            Swal.fire({
                title: 'Alerta',
                text: 'Funcionalidad no soportada por su navegador.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar',
            });
            return;
        }
        try {
            const position = await this.getCurrentPositionAsync();
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;

            if (this.miUbicacionMarker) {
                this.map.removeLayer(this.miUbicacionMarker);
            }

            this.miUbicacionMarker = L.marker([lat, lng], { icon: this.customIcon }).addTo(this.map).bindPopup('Mi ubicación');

            this.map.setView([lat, lng], 14);
            this.miUbicacionActivada = true;
        } catch (error) {
            console.error('Error obteniendo la ubicación:', error);
            Swal.fire({
                title: 'Error',
                text: 'No se pudo obtener la ubicación, verifique los permisos del navegador.',
                icon: 'error',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar',
            });
        }
    }

    private getCurrentPositionAsync(): Promise<GeolocationPosition> {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        });
    }

    removeLastMarker() {
        if (this.markers.length === 0) return;
        const lastMarker = this.markers[this.markers.length - 1];
        this.map.removeLayer(lastMarker);
        this.markers.pop();
    }

    removeMiUbicacion() {
        if (!this.miUbicacionMarker) return;
        this.map.removeLayer(this.miUbicacionMarker);
        this.miUbicacionMarker = null;
        this.miUbicacionActivada = false;
    }
}